import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { appModules } from "../components/global";
import Switch from "react-switch";
import { GET, POST, PUT, DELETE, loading, alert, format_date, DateInput } from "../components/CustomComponent";

const toISODate = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  return {
    date_from: toISODate(new Date(now.getFullYear(), now.getMonth(), 1)),
    date_to: toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

const getEmployeeName = (attendance) =>
  [
    attendance?.employee?.prefix_name,
    attendance?.employee?.first_name,
    attendance?.employee?.last_name,
  ]
    .filter(Boolean)
    .join(" ") || "-";

const formatTime = (value) => {
  if (!value) return "-";

  if (typeof value === "string") {
    const timeMatch = value.match(/^(\d{2}):(\d{2})/);
    if (timeMatch) {
      return `${timeMatch[1]}:${timeMatch[2]}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleTimeString("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatWorkingHours = (value) => {
  if (value === undefined || value === null || value === "") return "-";

  const totalHours = Number(value);
  if (Number.isNaN(totalHours)) return "-";

  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);

  if (hours === 0) return `${minutes} นาที`;
  if (minutes === 0) return `${hours} ชม.`;
  return `${hours} ชม. ${minutes} นาที`;
};

const getStatusBadgeClass = (status) => {
  if (status === "late") {
    return "badge bg-warning bg-opacity-10 text-warning rounded-pill px-3 py-2";
  }

  if (status === "absent" || status === "leave") {
    return "badge bg-danger bg-opacity-10 text-danger rounded-pill px-3 py-2";
  }

  return "badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-2";
};

const getStatusLabel = (status) => {
  const labels = {
    present: "ปกติ",
    late: "มาสาย",
    absent: "ขาดงาน",
    leave: "ลา",
    holiday: "วันหยุด",
  };

  return labels[status] || status || "-";
};

const AttendanceModal = ({ show, onHide, editData, onSave }) => {
  const [isActive, setIsActive] = useState(true);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
    <Modal.Header closeButton className="bg-light border-0">
      <Modal.Title className="fw-bold h5 mb-0">
        {editData ? "แก้ไขเวลาเข้าออกงาน" : "บันทึกเวลาเข้าออกงาน (Manual)"}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <form className="row g-3">
        <div className="col-md-12">
          <label className="form-label">ชื่อพนักงาน</label>
          <input type="text" className="form-control" defaultValue={editData?.name || ""} placeholder="ค้นหาพนักงาน..." />
        </div>
        <div className="col-md-6">
          <label className="form-label">เวลาเข้างาน</label>
          <input type="time" className="form-control" defaultValue={editData?.timeIn || ""} />
        </div>
        <div className="col-md-6">
          <label className="form-label">เวลาออกงาน</label>
          <input type="time" className="form-control" defaultValue={editData?.timeOut || ""} />
        </div>
         <div className="col-12">
          <label className="form-label mb-0 pointer" htmlFor="isActiveOvertime">เปิดใช้งาน (Active)</label><br/>
          <Switch checked={isActive} onChange={setIsActive} onColor="#0c75e6" height={24} width={48} id="isActiveOvertime" />
        </div>
      </form>
    </Modal.Body>
    <Modal.Footer className="bg-light border-0">
      <button type="button" className="btn btn-secondary" onClick={onHide}>ยกเลิก</button>
      <button type="button" className="btn btn-primary" onClick={() => onSave({ isActive })}>บันทึกข้อมูล</button>
    </Modal.Footer>
  </Modal>
  );
};

const defaultFilters = { searchText: "", ...getCurrentMonthRange() };

export default function Attendance() {
  const { title, description } = appModules["attendance"];
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [data, setData] = useState([]);

  const [searchInput, setSearchInput] = useState(defaultFilters.searchText);
  const [dateFromInput, setDateFromInput] = useState(defaultFilters.date_from);
  const [dateToInput, setDateToInput] = useState(defaultFilters.date_to);
  const [filters, setFilters] = useState(defaultFilters);

  const summaryCards = [
    {
      label: "เข้างานตรงเวลา",
      value: data.filter((item) => item?.status === "present").length,
      color: "#0c76e5",
    },
    {
      label: "มาสาย",
      value: data.filter((item) => item?.status === "late").length,
      color: "#e5a93e",
    },
    {
      label: "ขาด/ลา",
      value: data.filter((item) => ["absent", "leave"].includes(item?.status)).length,
      color: "#e53e3e",
    },
  ];

  const handleOpenModal = (data = null) => {
    setEditData(data);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setEditData(null);
    setShowModal(false);
  };

  const GetData = useCallback(async () => {
    loading(true);
    let body = {
      page: 1,
      page_size: 100,
      data_search: filters.searchText.trim(),
      date_from: filters.date_from,
      date_to: filters.date_to,
    };
    let result = await GET("v1/internal/attendance", body);
    if (result?.status) {
      setData(result.data || []);
    }
    loading(false);
  }, [filters]);

  useEffect(() => {
    GetData();
  }, [GetData]);

  const handleSearch = () => {
    setFilters({
      searchText: searchInput,
      date_from: dateFromInput,
      date_to: dateToInput,
    });
  };

  const handleResetFilters = () => {
    const reset = { searchText: "", ...getCurrentMonthRange() };
    setSearchInput(reset.searchText);
    setDateFromInput(reset.date_from);
    setDateToInput(reset.date_to);
    setFilters(reset);
  };

  const CreateUpdate = async (formData) => {
    if (
      await alert("", "info", "ยืนยัน", "ยืนยันการบันทึกข้อมูลหรือไม่?", "ยืนยัน", "ยกเลิก")
    ) {
      loading(true);
      let uid = formData?.uid || editData?.uid;
      let body = { ...formData };

      let result = !uid
        ? await POST("v1/internal/attendance", body)
        : await PUT("v1/internal/attendance/" + uid, body);
        
      if (result?.status === true) {
        alert("", "success", "สำเร็จ", "ทำรายการสำเร็จแล้ว");
        GetData();
        handleCloseModal();
      } else {
        alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
      }
      loading(false);
    }
  };

  const DeleteData = async (id) => {
    if (
      await alert("", "warning", "ลบรายการ", "ต้องการลบรายการนี้ใช่หรือไม่?", "ยืนยัน", "ยกเลิก")
    ) {
      loading(true);
      let result = await DELETE("v1/internal/attendance/" + id, null);
      if (result?.status) {
        alert("", "success", "สำเร็จ", "ทำรายการสำเร็จแล้ว");
        GetData();
      }
      loading(false);
    }
  };

  return (
      <AppShell title={title} description={description}>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start mb-4 gap-3">
            <div className="d-flex gap-2 flex-wrap align-items-center filters-mobile-stack">
              <input
                type="text"
                className="form-control"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && handleSearch()}
                placeholder="ค้นหาชื่อหรือรหัสพนักงาน..."
                style={{ width: "220px", borderRadius: "12px" }}
              />
              <DateInput
                className="form-control"
                value={dateFromInput}
                onChange={(event) => setDateFromInput(event.target.value)}
                title="ตั้งแต่วันที่"
                style={{ width: "160px", borderRadius: "12px" }}
              />
              <span className="text-secondary">ถึง</span>
              <DateInput
                className="form-control"
                value={dateToInput}
                onChange={(event) => setDateToInput(event.target.value)}
                title="ถึงวันที่"
                style={{ width: "160px", borderRadius: "12px" }}
              />
              <button type="button" className="btn btn-primary px-4" onClick={handleSearch} style={{ borderRadius: "12px" }}>
                ค้นหา
              </button>
              <button
                type="button"
                className="btn btn-light px-4"
                onClick={handleResetFilters}
                style={{ borderRadius: "12px", border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}
              >
                รีเซ็ต
              </button>
            </div>
          </div>

          {/* <div className="row g-3 mb-4">
              {summaryCards.map((item) => (
                  <div key={item.label} className="col-12 col-md-4">
                      <div
                          className="h-100 rounded-4 p-4 d-flex flex-column justify-content-center"
                          style={{
                              background: "linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%)",
                              border: "1px solid rgba(18, 125, 255, 0.10)",
                              boxShadow: "0 14px 28px rgba(12, 118, 229, 0.06)",
                          }}
                      >
                          <p className="small text-uppercase text-secondary mb-1">{item.label}</p>
                          <h2 className="h2 mb-0 fw-bold" style={{ color: item.color }}>
                              {item.value} <span className="h6 text-secondary fw-normal">คน</span>
                          </h2>
                      </div>
                  </div>
              ))}
          </div> */}

          <div
              className="rounded-4 p-3 bg-white"
              style={{
                  border: "1px solid rgba(18, 125, 255, 0.10)",
                  boxShadow: "0 4px 12px rgba(12, 118, 229, 0.04)",
              }}
          >
              <h5 className="mb-4 fw-semibold text-dark">บันทึกเวลาเข้า-ออกงาน (ประจำวัน)</h5>
              <div className="table-responsive table-mobile-cards">
                  <table className="table table-hover align-middle">
                      <thead className="table-light">
                          <tr>
                              <th className="text-secondary fw-medium">วันที่</th>
                              <th className="text-secondary fw-medium">ช่วงเวลา</th>
                              <th className="text-secondary fw-medium rounded-start">ชื่อพนักงาน</th>
                              <th className="text-secondary fw-medium">เวลาเข้า</th>
                              <th className="text-secondary fw-medium">เวลาออก</th>
                              <th className="text-secondary fw-medium">ชั่วโมงทำงาน</th>
                          </tr>
                      </thead>
                      <tbody>
                          {data.length > 0 ? (
                              data.map((item) => (
                                  <tr key={item.uid}>
                                      <td data-label="วันที่">{format_date(item.attendance_date, "dd/mm/yyyy") || "-"}</td>
                                      <td data-label="ช่วงเวลา">
                                          {formatTime(item.working_start_time)} - {formatTime(item.working_end_time)} น.
                                      </td>
                                      <td data-label="ชื่อพนักงาน">{getEmployeeName(item)}</td>
                                      <td data-label="เวลาเข้า">{item.check_in_at ? format_date(item.check_in_at, "dd/mm/yyyy hh:mm") : "-"}</td>
                                      <td data-label="เวลาออก">{item.check_out_at ? format_date(item.check_out_at, "dd/mm/yyyy hh:mm") : "-"}</td>
                                      <td data-label="ชั่วโมงทำงาน">{formatWorkingHours(item.actual_working_hours)}</td>
                                  </tr>
                              ))
                          ) : (
                              <tr>
                                  <td colSpan="6" className="text-center py-4 text-secondary">
                                      ไม่พบข้อมูลเวลาเข้าออกงาน
                                  </td>
                              </tr>
                          )}
                      </tbody>
                  </table>
              </div>
          </div>

          <AttendanceModal show={showModal} onHide={handleCloseModal} editData={editData} onSave={CreateUpdate} />
      </AppShell>
  );
}
